/**
 * PlayerService - Manages player identity with localStorage persistence
 */

import type { EventBus } from '@/core/EventBus';

export const PlayerEvents = {
  NAME_CHANGED: 'player:nameChanged',
} as const;

const STORAGE_KEY = 'character_battler_player_name';
const DEFAULT_NAME = 'Player';
const MIN_LENGTH = 2;
const MAX_LENGTH = 16;
const VALID_PATTERN = /^[a-zA-Z0-9 \-_]+$/;

export interface ValidationResult {
  success: boolean;
  error?: string;
}

export class PlayerService {
  private eventBus: EventBus;
  private cachedName: string | null = null;
  private storageAvailable: boolean;

  constructor(eventBus: EventBus) {
    this.eventBus = eventBus;
    this.storageAvailable = this.checkStorageAvailable();

    // Load cached name on init
    if (this.storageAvailable) {
      this.cachedName = localStorage.getItem(STORAGE_KEY);
    }
  }

  /**
   * Check if localStorage is available
   */
  private checkStorageAvailable(): boolean {
    try {
      const testKey = '__storage_test__';
      localStorage.setItem(testKey, testKey);
      localStorage.removeItem(testKey);
      return true;
    } catch {
      console.warn('[PlayerService] localStorage not available, using in-memory storage');
      return false;
    }
  }

  /**
   * Get the player's name
   */
  getName(): string {
    return this.cachedName || DEFAULT_NAME;
  }

  /**
   * Check if player has set a name
   */
  hasName(): boolean {
    return this.cachedName !== null;
  }

  /**
   * Validate a name against rules
   */
  validateName(name: string): ValidationResult {
    const trimmed = name.trim();

    if (trimmed.length < MIN_LENGTH) {
      return { success: false, error: `Name must be at least ${MIN_LENGTH} characters` };
    }

    if (trimmed.length > MAX_LENGTH) {
      return { success: false, error: `Name must be at most ${MAX_LENGTH} characters` };
    }

    if (!VALID_PATTERN.test(trimmed)) {
      return { success: false, error: 'Name can only contain letters, numbers, spaces, hyphens, and underscores' };
    }

    return { success: true };
  }

  /**
   * Set the player's name
   */
  setName(name: string): ValidationResult {
    const validation = this.validateName(name);
    if (!validation.success) {
      return validation;
    }

    const trimmed = name.trim();
    this.cachedName = trimmed;

    if (this.storageAvailable) {
      try {
        localStorage.setItem(STORAGE_KEY, trimmed);
      } catch (e) {
        console.warn('[PlayerService] Failed to save name to localStorage:', e);
      }
    }

    this.eventBus.emit(PlayerEvents.NAME_CHANGED, { name: trimmed });

    return { success: true };
  }
}
